import { useEffect, useState, useRef, useCallback } from "react";
import Select from "react-select";
import DumbbellPlot from "./DumbbellPlot";
import {
  formateTimeRange,
  loadFromState,
  resetState,
  saveToState,
} from "../utils";
import { getProfiles } from "../api/quontxApi";

const DumbbellPlotFilters = ({
  timestampBuckets,
  domains,
  proxies,
  isLoading,
  propagnateData,
}) => {
  const maxDomainCount = 2;
  const maxEntityCount = 5;
  const propagnateFunctionRef = useRef(propagnateData);

  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  const [activeDomains, setActiveDomains] = useState([]);
  const [activeProxies, setActiveProxies] = useState([]);
  const [activeTimestampTo, setActiveTimestampTo] = useState(null);
  const [activeTimestampFrom, setActiveTimestampFrom] = useState(null);
  const [timestampFromOptions, setTimestampFromOptions] = useState([]);
  const [timestampToOptions, setTimestampToOptions] = useState([]);
  const [activeTimestampBuckets, setActiveTimestampBuckets] = useState([]);

  const [domainSelectDisabled, setDomainSelectDisabled] = useState(false);
  const [proxySelectDisabled, setProxySelectDisabled] = useState(false);

  const manageActiveDomains = (selectedDomains) => {
    if (selectedDomains.length <= maxDomainCount) {
      setDomainSelectDisabled(false);
      setActiveDomains(
        selectedDomains.map((domain) => {
          return { label: domain.label, value: domain.value };
        })
      );
      return;
    }
    setDomainSelectDisabled(true);
  };

  const manageActiveProxies = (selectedEntities) => {
    if (
      selectedEntities.length + (activeProxies ? activeProxies.length : 0) <=
      maxEntityCount
    ) {
      setActiveProxies(
        selectedEntities.map((entity) => {
          return { label: entity.label, value: entity.value };
        })
      );
    }
  };

  useEffect(() => {
    setActiveTimestampBuckets(
      [
        timestampBuckets.indexOf(activeTimestampFrom?.value),
        timestampBuckets.indexOf(activeTimestampTo?.value),
      ].sort((a, b) => a - b)
    );
  }, [activeTimestampFrom, activeTimestampTo, timestampBuckets]);

  // Disable options of the "To" dropdown according to the selected option in the "From" dropdown
  useEffect(() => {
    setTimestampToOptions(
      timestampBuckets.map((timestampBucket, idx) => {
        return {
          label: formateTimeRange(timestampBucket),
          value: timestampBucket,
          isDisabled:
            idx < 10 ||
            timestampBuckets.indexOf(timestampBucket) <
              timestampBuckets.indexOf(activeTimestampFrom?.value),
        };
      })
    );
  }, [activeTimestampFrom, timestampBuckets]);

  // Disable options of the "From" dropdown according to the selected option in the "To" dropdown
  useEffect(() => {
    setTimestampFromOptions(
      timestampBuckets.map((timestampBucket, idx) => {
        return {
          label: formateTimeRange(timestampBucket),
          value: timestampBucket,
          isDisabled:
            idx < 10 ||
            (activeTimestampTo &&
              timestampBuckets.indexOf(timestampBucket) >
                timestampBuckets.indexOf(activeTimestampTo?.value)) ||
            timestampBuckets.length - 1 === idx,
        };
      })
    );
  }, [activeTimestampTo, timestampBuckets]);

  // Init both timestamp bucket dropdowns with its options
  useEffect(() => {
    setTimestampFromOptions(
      timestampBuckets.map((timestampBucket, idx) => {
        return {
          label: formateTimeRange(timestampBucket),
          value: timestampBucket,
          isDisabled: idx < 10 || timestampBuckets.length - 1 === idx,
        };
      })
    );

    setTimestampToOptions(
      timestampBuckets.map((timestampBucket, idx) => {
        return {
          label: formateTimeRange(timestampBucket),
          value: timestampBucket,
          isDisabled: idx < 10,
        };
      })
    );
  }, [timestampBuckets]);

  const resetFilters = () => {
    resetState("dumbbellPlotState", [
      {
        stateFunc: setActiveDomains,
        nullVal: [],
      },
      {
        stateFunc: setActiveProxies,
        nullVal: [],
      },
      {
        stateFunc: setActiveTimestampBuckets,
        nullVal: [],
      },
      {
        stateFunc: setActiveTimestampFrom,
        nullVal: null,
      },
      {
        stateFunc: setActiveTimestampTo,
        nullVal: null,
      },
    ]);
    setDomainSelectDisabled(false);
    setProxySelectDisabled(false);
  };

  const loadProfiles = useCallback(async () => {
    const profiles = await getProfiles({
      itemsPerPage: maxEntityCount,
      entities: activeProxies.map((proxies) => proxies.value),
    });
    propagnateFunctionRef.current(profiles.data);

    // Create key value pairs for the data
    profiles.data.data.forEach((profile) => {
      data[profile.metadata.corpora.name] = profile.profile;
    });
    setData(data);
  }, [activeProxies, data]);

  useEffect(() => {
    loadFromState("dumbbellPlotState", [
      {
        stateFunc: setActiveDomains,
        storageObjectKey: "activeDomains",
        defaultVal: [],
      },
      {
        stateFunc: setActiveProxies,
        storageObjectKey: "activeProxies",
        defaultVal: [],
      },
      {
        stateFunc: setActiveTimestampFrom,
        storageObjectKey: "activeTimestampFrom",
        defaultVal: null,
      },
      {
        stateFunc: setActiveTimestampTo,
        storageObjectKey: "activeTimestampTo",
        defaultVal: null,
      },
    ]);

    const propagnateDataFunctionCopy = propagnateFunctionRef.current;
    return () => {
      propagnateDataFunctionCopy({});
    };
  }, []);

  useEffect(() => {
    if (
      activeTimestampFrom !== null ||
      activeTimestampTo !== null ||
      activeDomains.length > 0 ||
      activeProxies.length > 0
    ) {
      saveToState("dumbbellPlotState", {
        activeTimestampFrom: activeTimestampFrom,
        activeTimestampTo: activeTimestampTo,
        activeDomains: activeDomains,
        activeProxies: activeProxies,
      });
    }
  }, [activeTimestampFrom, activeTimestampTo, activeDomains, activeProxies]);

  useEffect(() => {
    if (activeProxies.length === 0) return;
    (async () => {
      setLoadingData(true);
      await loadProfiles();
      setLoadingData(false);
    })();
  }, [activeProxies, loadProfiles]);

  return (
    <div className="w-full flex flex-col items-center content-center">
      {isLoading && <p>Loading data...</p>}
      {!isLoading && data && proxies && domains && (
        <div className="flex flex-col gap-4 items-center justify-center mb-8">
          <div className="flex gap-4  justify-center mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Organization Proxiles</p>
              <Select
                isMulti={true}
                isSearchable={true}
                value={activeProxies}
                onChange={(selected) => manageActiveProxies(selected)}
                options={proxies.map((proxy) => {
                  return {
                    label: proxy.name,
                    value: proxy.uuid,
                    isDisabled: proxySelectDisabled,
                  };
                })}
              />
            </div>
            <div className="w-[240px] flex flex-col gap-2">
              <p>Societal Domains</p>
              <Select
                isMulti={true}
                isSearchable={true}
                value={activeDomains}
                onChange={(selected) => manageActiveDomains(selected)}
                options={domains.map((domain) => {
                  return {
                    label: domain.description,
                    value: domain.uuid,
                    isDisabled: domainSelectDisabled,
                  };
                })}
              />
            </div>
          </div>
          <div className="flex gap-4 justify-center mb-8">
            <div className="w-[240px] flex flex-col gap-2">
              <p>Timestamp Bucket From</p>
              <Select
                isSearchable={true}
                defaultValue={activeTimestampFrom}
                value={activeTimestampFrom}
                onChange={(selected) => setActiveTimestampFrom(selected)}
                options={timestampFromOptions}
              />
            </div>
            <div className="w-[240px] flex flex-col gap-2">
              <p>Timestamp Bucket To</p>
              <Select
                isSearchable={true}
                defaultValue={activeTimestampTo}
                value={activeTimestampTo}
                onChange={(selected) => setActiveTimestampTo(selected)}
                options={timestampToOptions}
              />
            </div>
          </div>
          <div>
            <button
              onClick={() => resetFilters()}
              className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
            >
              Reset filters
            </button>
          </div>
          {activeDomains && activeProxies && data && activeTimestampBuckets && (
            <div className="flex items-center content-center flex-wrap">
              <DumbbellPlot
                isLoading={loadingData}
                data={data}
                timebucketIdxs={activeTimestampBuckets}
                activeDomains={activeDomains}
                activeEntities={[]}
                activeProxies={activeProxies}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DumbbellPlotFilters;
